import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const bigoServices = [
  { icon: "ri-user-add-line", title: "Recruitment", desc: "Finding and attracting talented creators to the platform." },
  { icon: "ri-door-open-line", title: "Onboarding", desc: "Smooth transition and setup for new hosts joining BIGO Live." },
  { icon: "ri-graduation-cap-line", title: "Training", desc: "Comprehensive coaching on content, engagement, and compliance." },
  { icon: "ri-bar-chart-2-line", title: "Performance Support", desc: "Ongoing tracking and coaching to maximize every stream." },
  { icon: "ri-heart-add-line", title: "Creator Retention", desc: "Systems and support designed to keep creators motivated and growing." },
];

const platformCategories = [
  {
    icon: "ri-instagram-line",
    name: "Instagram",
    type: "Social Media",
    desc: "Content strategy, reels, brand alignment, and growth tactics for Instagram creators and businesses.",
    color: "from-rose-500/20 to-orange-400/10",
  },
  {
    icon: "ri-tiktok-line",
    name: "TikTok",
    type: "Short-form Video",
    desc: "Viral content frameworks, trends coaching, and monetization strategy for TikTok creators.",
    color: "from-cyan-400/20 to-pink-500/10",
  },
  {
    icon: "ri-youtube-line",
    name: "YouTube",
    type: "Long-form Video",
    desc: "Channel growth, audience retention, SEO strategy, and AdSense monetization coaching.",
    color: "from-red-500/20 to-orange-500/10",
  },
  {
    icon: "ri-twitter-x-line",
    name: "X (Twitter)",
    type: "Social Media",
    desc: "Brand voice development, audience building, and engagement strategy on X.",
    color: "from-white/10 to-white/5",
  },
  {
    icon: "ri-live-line",
    name: "Live Streaming Apps",
    type: "Live Platforms",
    desc: "Multi-platform live streaming support across various apps beyond BIGO Live.",
    color: "from-gold/15 to-gold/5",
  },
  {
    icon: "ri-store-2-line",
    name: "Brand Collabs",
    type: "Partnerships",
    desc: "We connect creators with brands across any platform for sponsored content and campaigns.",
    color: "from-emerald-400/15 to-emerald-400/5",
  },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Platforms & Apps | The LittyCity Agency LLC",
  url: `${import.meta.env.VITE_SITE_URL || "https://example.com"}/platforms`,
  description:
    "The LittyCity Agency LLC operates across Instagram, TikTok, YouTube, X, BIGO Live, and more. One creator management agency, every platform.",
  mainEntity: {
    "@type": "Organization",
    name: "The LittyCity Agency LLC",
    url: import.meta.env.VITE_SITE_URL || "https://example.com",
    knowsAbout: [
      "BIGO Live",
      "Instagram",
      "TikTok",
      "YouTube",
      "X (Twitter)",
      "Live Streaming",
      "Brand Collaborations",
    ],
  },
};

export default function PlatformsPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    const id = "schema-platforms";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA);
    document.title = "Platforms & Apps | The LittyCity Agency LLC";

    return () => {
      observer.disconnect();
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-brand-black">
      <Navbar />

      {/* Header */}
      <div className="pt-36 pb-20 bg-brand-black text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-gold/4 blur-3xl" />
        </div>
        <div className="relative z-10 reveal">
          <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase block mb-5">
            Where We Operate
          </span>
          <h1 className="font-display font-black text-white text-5xl md:text-7xl leading-none mb-6">
            Platforms <span className="text-gradient-gold">&amp; Apps</span>
          </h1>
          <p className="text-white/50 font-sans text-lg max-w-2xl mx-auto leading-relaxed">
            We operate across the full social media landscape — from live streaming to short-form video,
            social platforms to brand partnerships. One agency, every platform.
          </p>
          <div className="w-24 h-px bg-gold mx-auto mt-8" />
        </div>
      </div>

      <main>
        {/* Multi-Platform Grid */}
        <section className="bg-brand-dark py-24 md:py-32 section-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16 reveal">
              <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase block mb-4">
                Our Platform Reach
              </span>
              <h2 className="font-display font-bold text-white text-4xl md:text-5xl mb-4">
                We Work <span className="text-gradient-gold">Everywhere</span> You Create
              </h2>
              <p className="text-white/50 font-sans text-lg max-w-2xl mx-auto">
                The LittyCity Agency isn&apos;t limited to one app. We meet creators where they are and help them win on every platform.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {platformCategories.map((platform, i) => (
                <div
                  key={platform.name}
                  className={`reveal reveal-delay-${(i % 4) + 1} card-gold bg-brand-black rounded-2xl p-8 group cursor-default overflow-hidden relative`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl border border-gold/30 bg-gold/5 group-hover:border-gold/60 group-hover:bg-gold/10 transition-all duration-300">
                        <i className={`${platform.icon} text-gold text-2xl`} />
                      </div>
                      <span className="px-3 py-1 rounded-full border border-gold/20 text-gold/60 text-xs font-sans font-medium">
                        {platform.type}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-white text-xl mb-3 group-hover:text-gold transition-colors duration-300">
                      {platform.name}
                    </h3>
                    <p className="text-white/50 font-sans text-sm leading-relaxed">
                      {platform.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BIGO Live Section */}
        <section className="bg-brand-black py-24 md:py-36 relative overflow-hidden section-divider">
          {/* BIGO watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <span
              className="font-display font-black text-gold/3"
              style={{ fontSize: "clamp(8rem, 25vw, 22rem)", whiteSpace: "nowrap" }}
            >
              BIGO
            </span>
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16 reveal">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold text-white mb-8">
                <i className="ri-verified-badge-fill text-base" />
                <span className="font-sans font-bold text-sm tracking-widest uppercase">
                  Official Partner
                </span>
              </div>
              <h2 className="font-display font-black text-white text-5xl md:text-6xl mb-4">
                BIGO <span className="text-gradient-gold">Live</span>
              </h2>
              <p className="text-white/55 font-sans text-lg max-w-2xl mx-auto leading-relaxed">
                One of our flagship platforms. As an official BIGO Live agency partner,
                we provide end-to-end creator lifecycle management — from first stream to top earner.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {bigoServices.map((svc, i) => (
                <div
                  key={svc.title}
                  className={`reveal reveal-delay-${(i % 4) + 1} card-gold bg-brand-card rounded-2xl p-6 text-center group hover:bg-gold/90 transition-all duration-400 cursor-default`}
                >
                  <div className="w-14 h-14 flex items-center justify-center mx-auto rounded-xl border border-gold/30 bg-gold/5 mb-4 group-hover:bg-white/15 group-hover:border-white/20 transition-all duration-400">
                    <i className={`${svc.icon} text-gold text-xl group-hover:text-white transition-colors duration-400`} />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg mb-2 group-hover:text-white transition-colors duration-400">
                    {svc.title}
                  </h3>
                  <p className="text-white/50 text-xs font-sans leading-relaxed group-hover:text-white/80 transition-colors duration-400">
                    {svc.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-14 text-center reveal">
              <Link
                to="/join-bigo"
                className="btn-gold inline-flex items-center gap-3 px-10 py-4 rounded-full text-white font-sans font-semibold text-sm tracking-widest cursor-pointer whitespace-nowrap"
              >
                <i className="ri-live-line text-lg" />
                Join as a BIGO Host Today
              </Link>
            </div>
          </div>
        </section>

        {/* Partnership CTA */}
        <section className="bg-brand-dark py-24 md:py-36 section-divider">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="reveal text-center">
              <div className="card-gold bg-brand-black rounded-3xl p-14">
                <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full border border-gold/40 bg-gold/5 mb-8">
                  <i className="ri-global-line text-gold text-3xl" />
                </div>
                <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase block mb-4">
                  Always Growing
                </span>
                <h3 className="font-display font-bold text-white text-3xl md:text-4xl mb-4">
                  New Platforms, <span className="text-gradient-gold">New Opportunities</span>
                </h3>
                <p className="text-white/55 font-sans text-base mb-10 max-w-xl mx-auto leading-relaxed">
                  The LittyCity Agency is always expanding its platform partnerships. If you have an app or platform
                  that aligns with our creator-first mission, let&apos;s connect.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-sans font-semibold text-sm tracking-widest cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shake-hands-line" />
                    Partner With Us
                  </Link>
                  <Link
                    to="/join-bigo"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-gold text-gold font-sans font-medium text-sm tracking-widest hover:bg-gold hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line" />
                    Join Our Network
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
